import React, { useEffect, useState } from 'react';
import { useBookingCartStore } from '../stores/bookingCartStore';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useI18n } from '../i18n/i18nContext';
import { serviceAPI, getAvailableTimes } from '../services/api';
import { getTranslatedField } from '../utils/translationUtils';
import { formatInTimeZone } from 'date-fns-tz';
import { Service } from '@shared/types';

const TALLINN_TIMEZONE = 'Europe/Tallinn';

interface SuggestedService extends Service {
  availableAt: string;
}

export const SuggestedServices: React.FC = () => {
  const { items } = useBookingCartStore();
  const navigate = useLocalizedNavigate();
  const { t, language } = useI18n();
  const [suggestedServices, setSuggestedServices] = useState<SuggestedService[]>([]);
  const [loading, setLoading] = useState(false);

  // Create a dependency key that only changes for meaningful updates
  // (not for invitee email changes or quantity changes)
  const itemsKey = items.map(item =>
    `${item.serviceId}-${item.startTime}-${item.endTime}`
  ).join('|');

  useEffect(() => {
    if (items.length > 0) {
      fetchSuggestedServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey]);

  const fetchSuggestedServices = async () => {
    setLoading(true);
    try {
      const lastItem = items[items.length - 1];
      const endTime = new Date(lastItem.endTime);
      const searchDate = formatInTimeZone(endTime, TALLINN_TIMEZONE, 'yyyy-MM-dd');
      const searchTime = formatInTimeZone(endTime, TALLINN_TIMEZONE, 'HH:mm');

      const servicesResponse = await serviceAPI.getAll();
      const activeServices = servicesResponse.data.filter((service: any) => 
        service.metadata?.is_active === true && service.id !== lastItem.serviceId
      );

      const availabilityChecks = activeServices.map(async (service: Service) => {
        try {
          const availabilityResponse = await getAvailableTimes(service.id, searchDate, searchDate);
          const slotsData = availabilityResponse.data.data || [];
          
          const matchingSlot = slotsData.find((slot: any) => {
            const slotTime = slot.time || slot;
            return slotTime === searchTime;
          });

          if (matchingSlot) {
            return { ...service, availableAt: searchTime };
          }
        } catch (err) {
          console.error(`Error checking availability for service ${service.id}:`, err);
        }
        return null;
      });

      const results = await Promise.all(availabilityChecks);
      const validSuggestions = results.filter((result): result is SuggestedService => result !== null);

      setSuggestedServices(validSuggestions.slice(0, 3));
    } catch (err) {
      console.error('Error fetching suggested services:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceClick = (service: SuggestedService) => {
    navigate(`/book/${service.id}`);
  };

  if (items.length === 0 || loading || suggestedServices.length === 0) {
    return null;
  }

  return (
    <div className="bg-blue-50 rounded-lg shadow-md p-6 border border-blue-200 mt-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">{t.cart.suggestedServices}</h3>
      <p className="text-sm text-gray-600 mb-4">{t.cart.suggestedServicesDescription}</p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {suggestedServices.map((service) => (
          <div
            key={service.id}
            onClick={() => handleServiceClick(service)}
            className="bg-white rounded-lg p-4 cursor-pointer hover:shadow-lg transition-shadow border border-gray-200"
          >
            {service.image && (
              <img
                src={service.image}
                alt={getTranslatedField(service, 'name', language)}
                className="w-full h-24 object-cover rounded-md mb-3"
              />
            )}
            <h4 className="font-semibold text-gray-900 mb-2">
              {getTranslatedField(service, 'name', language)}
            </h4>
            <p className="text-sm text-gray-600 mb-2">
              {t.cart.available} {service.availableAt}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
